import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the remote-work-byod start screen. Three
 * short slides (what/who, main risks, key terms) shown in the reader while the
 * 3D room downloads. Content only; the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. device check: approved work laptop vs. an unchecked personal tablet */
/* ------------------------------------------------------------------ */
function DeviceRow({ y, label, value }: { y: number; label: string; value: string }): JSX.Element {
  return (
    <g>
      <circle cx="26" cy={y} r="7" fill={C.mint} fillOpacity="0.16" />
      <polyline
        points={`22.5,${y} 25,${y + 2.6} 29.5,${y - 2.8}`}
        fill="none"
        stroke={C.mint}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text x="42" y={y + 4.2} fontSize="12.5" fill={C.text}>
        {txv(label)}
      </text>
      <text x="334" y={y + 4.2} fontSize="12" fill={C.muted} textAnchor="end">
        {txv(value)}
      </text>
    </g>
  );
}

function EmptyCheck({ y, label }: { y: number; label: string }): JSX.Element {
  return (
    <g>
      <rect x="32.5" y={y - 7.5} width="13" height="13" rx="3" fill="none" stroke={C.muted} />
      <text x="54" y={y + 3.5} fontSize="11.5" fill={C.soft}>
        {txv(label)}
      </text>
    </g>
  );
}

function DeviceArt(): JSX.Element {
  const H = 296;
  return (
    <Frame
      label={tx("Example: device check")}
      aria={tx("A work laptop that passes its security checks, and a personal tablet that still needs the company rule and controls checked")}
      height={H}
    >
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Work laptop")}
      </text>
      <rect
        className="sr-anim-pulse"
        x="250"
        y="37"
        width="94"
        height="24"
        rx="12"
        fill={C.mint}
        fillOpacity="0.14"
        stroke={C.mint}
        strokeOpacity="0.45"
      />
      <polyline
        points="262,49 265,52 270,46"
        fill="none"
        stroke={C.mint}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text x="278" y="53.5" fontSize="11" fontWeight="700" fill={C.mint} letterSpacing="0.6">
        {tx("APPROVED")}
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      <DeviceRow y={92} label={tx("Disk encrypted")} value="On" />
      <line x1="16" y1="106.5" x2="344" y2="106.5" stroke={C.line} strokeOpacity="0.6" />
      <DeviceRow y={122} label={tx("Screen lock")} value="On" />
      <line x1="16" y1="136.5" x2="344" y2="136.5" stroke={C.line} strokeOpacity="0.6" />
      <DeviceRow y={152} label={tx("Updates")} value="Current" />

      <rect x="16.5" y="172.5" width="327" height="108" rx="8" fill={C.amber} fillOpacity="0.08" stroke={C.amber} strokeOpacity="0.4" />
      <rect x="16" y="178" width="3" height="97" rx="1.5" fill={C.amber} />
      <circle className="sr-anim-ring" cx="33" cy="195" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="33" cy="195" r="1.8" fill={C.amber} />
      <text x="48" y="199" fontSize="12.5" fontWeight="700" fill={C.amber}>
        {tx("Personal tablet")}
      </text>
      <text x="32" y="217" fontSize="11" fill={C.muted}>
        {tx("You own it. That alone is not approval.")}
      </text>
      <EmptyCheck y={238} label={tx("Company rule allows it")} />
      <EmptyCheck y={260} label={tx("Required controls are on")} />
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. risks: lookalike Wi-Fi under a scan + an urgent call to verify   */
/* ------------------------------------------------------------------ */
function WifiGlyph({ x, y, color }: { x: number; y: number; color: string }): JSX.Element {
  return (
    <g transform={`translate(${x} ${y})`} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round">
      <path d="M-8 -2 A11 11 0 0 1 8 -2" />
      <path d="M-4.6 1.6 A6.5 6.5 0 0 1 4.6 1.6" />
      <circle cx="0" cy="5" r="1" fill={color} />
    </g>
  );
}

function RiskArt(): JSX.Element {
  const H = 300;
  return (
    <Frame
      label={tx("Example: away from the office")}
      aria={tx("A list of Wi-Fi networks where a copied lookalike name is flagged, and an urgent call from a boss asking for a file that must be verified first")}
      height={H}
    >
      <defs>
        <clipPath id="sr-byod-wifi-clip">
          <rect x="16.5" y="132.5" width="327" height="40" rx="8" />
        </clipPath>
      </defs>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Wi-Fi networks")}
      </text>
      <text x="344" y="53" fontSize="11" fill={C.muted} textAnchor="end">
        {tx("Airport")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <rect x="16.5" y="82.5" width="327" height="40" rx="8" fill={C.panel2} stroke={C.line} />
      <WifiGlyph x={38} y={100} color={C.mint} />
      <text x="58" y="106" fontSize="12" fontFamily={MONO} fill={C.text}>
        {tx("Airport-WiFi")}
      </text>
      <text x="334" y="106" fontSize="10.5" fill={C.mint} textAnchor="end">
        {tx("Saved, trusted")}
      </text>

      <rect x="16.5" y="132.5" width="327" height="40" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.45" />
      <g clipPath="url(#sr-byod-wifi-clip)">
        <rect className="sr-anim-sweep" x="16" y="132" width="56" height="41" fill={C.red} fillOpacity="0.16" />
      </g>
      <WifiGlyph x={38} y={150} color={C.red} />
      <text x="58" y="156" fontSize="12" fontFamily={MONO} fill={C.text}>
        {tx("Airport_Free_WiFi")}
      </text>
      <text x="334" y="156" fontSize="10.5" fill={C.red} textAnchor="end">
        {tx("Open, copied name")}
      </text>

      <rect x="16.5" y="186.5" width="327" height="96" rx="8" fill={C.code} stroke={C.line} />
      <circle cx="40" cy="212" r="12" fill={C.mint} fillOpacity="0.12" />
      <circle cx="40" cy="209" r="3.6" fill="none" stroke={C.mint} strokeWidth="1.5" />
      <path d="M32.5 219 a7.5 6 0 0 1 15 0" fill="none" stroke={C.mint} strokeWidth="1.5" strokeLinecap="round" />
      <text x="62" y="209" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Incoming call: “CEO”")}
      </text>
      <text x="62" y="225" fontSize="11" fill={C.muted}>
        {tx("Urgent. Send me the file now.")}
      </text>
      <rect x="28.5" y="241.5" width="303" height="30" rx="7" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="44" cy="256.5" r="5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="44" cy="256.5" r="1.7" fill={C.amber} />
      <text x="58" y="260.5" fontSize="11.5" fontWeight="600" fill={C.amber}>
        {tx("Verify on a channel you already trust")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. terms: grid of icon chips                                        */
/* ------------------------------------------------------------------ */
type Glyph = "byod" | "vpn" | "hotspot" | "lookalike" | "deepfake" | "report";

function GlyphShape({ g }: { g: Glyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "byod":
      return (
        <g {...s}>
          <rect x="-4.5" y="-8" width="9" height="16" rx="2" />
          <line x1="-1.6" y1="5" x2="1.6" y2="5" />
        </g>
      );
    case "vpn":
      return (
        <g {...s}>
          <path d="M0 -8 L7 -5 V1 C7 5 3.5 7.5 0 9 C-3.5 7.5 -7 5 -7 1 V-5 Z" />
          <polyline points="-3,0.5 -1,3 3,-2" />
        </g>
      );
    case "hotspot":
      return (
        <g {...s}>
          <path d="M-8 -2 A11 11 0 0 1 8 -2" />
          <path d="M-4.6 1.6 A6.5 6.5 0 0 1 4.6 1.6" />
          <circle cx="0" cy="5.5" r="1" fill={C.mint} />
        </g>
      );
    case "lookalike":
      return (
        <g {...s}>
          <rect x="-8.5" y="-7" width="11" height="12" rx="2" />
          <rect x="-2.5" y="-3" width="11" height="12" rx="2" />
        </g>
      );
    case "deepfake":
      return (
        <g {...s}>
          <circle cx="0" cy="0" r="7.5" />
          <circle cx="-2.6" cy="-1.6" r="0.8" fill={C.mint} />
          <circle cx="2.6" cy="-1.6" r="0.8" fill={C.mint} />
          <path d="M-3 3 Q0 5.2 3 3" />
        </g>
      );
    case "report":
      return (
        <g {...s}>
          <line x1="-5" y1="-8" x2="-5" y2="8" />
          <path d="M-5 -8 H6 L3 -4 L6 0 H-5" />
        </g>
      );
  }
}

interface ChipSpec {
  g: Glyph;
  title: string;
  sub: string;
}

const CHIPS: ChipSpec[] = [
  { g: "byod", title: "BYOD", sub: "your own device" },
  { g: "vpn", title: "VPN", sub: "private tunnel" },
  { g: "hotspot", title: "Hotspot", sub: "phone as Wi-Fi" },
  { g: "lookalike", title: "Lookalike Wi-Fi", sub: "copied name" },
  { g: "deepfake", title: "Deepfake", sub: "faked voice or face" },
  { g: "report", title: "Incident report", sub: "tell IT early" },
];

function TermsArt(): JSX.Element {
  const H = 296;
  return (
    <Frame
      label={tx("Terms at a glance")}
      aria={tx("Six terms shown as labelled icon chips: BYOD, VPN, hotspot, lookalike Wi-Fi, deepfake and incident report")}
      height={H}
    >
      {CHIPS.map((c, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = 16 + col * 172;
        const y = 38 + row * 84;
        return (
          <g key={c.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width="155" height="71" rx="10" fill={C.panel2} stroke={C.line} />
            <g className="sr-anim-float" style={{ animationDelay: `${i * 0.35}s` }}>
              <circle cx="28" cy="36" r="17" fill={C.mint} fillOpacity="0.12" />
              <g transform="translate(28 36)">
                <GlyphShape g={c.g} />
              </g>
            </g>
            <text x="54" y="33" fontSize="12.5" fontWeight="700" fill={C.text}>
              {txv(c.title)}
            </text>
            <text x="54" y="50" fontSize="10.5" fill={C.muted}>
              {txv(c.sub)}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

export const REMOTE_WORK_BYOD_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Your desk moves. The rules come with you.",
    illustration: DeviceArt,
    caption: "Owning a device is not the same as approval.",
    sections: [
      {
        h: "What this is about",
        body: "A day working away from the office, on your own device. Watch a short film, practise Max\u2019s remote day, then answer five questions.",
      },
      {
        h: "Who it\u2019s for",
        body: "Anyone who works remotely, travels, or uses a personal phone, tablet or laptop for work.",
      },
      {
        h: "Why your judgement matters",
        body: "No one is watching your network, your screen or your call. The rules only work if you follow them where nobody checks.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "Where a day away from the office goes wrong.",
    illustration: RiskArt,
    caption: "A familiar name or face is not proof.",
    sections: [
      {
        h: "Lookalike and public Wi-Fi",
        body: "A copied network name looks official. Use a network you trust, a hotspot or a VPN.",
      },
      {
        h: "Calls and screens in public",
        body: "Cafes and trains leak audio and screens. Move somewhere private, or keep it general.",
      },
      {
        h: "Personal devices",
        body: "Owning it is not approval. The company rule and its required controls come first.",
      },
      {
        h: "Urgent requests and fake faces",
        body: "A voice or face on a call can be faked. Verify on a channel you already trust.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "BYOD", body: "Bring your own device: using a personal phone, tablet or laptop for work, only where company rules allow it." },
      { h: "VPN", body: "A private, encrypted tunnel to the company network, so others on the same Wi-Fi cannot read your traffic." },
      { h: "Hotspot", body: "Sharing your phone\u2019s mobile data as a Wi-Fi network, often safer than a public one." },
      { h: "Lookalike Wi-Fi", body: "A network with a copied, familiar name, set up to capture what you send." },
      { h: "Deepfake", body: "A faked voice or video of a real person, used to pressure you into acting fast." },
      { h: "Incident report", body: "Telling IT or security right away when a device is lost or something looks wrong. Early reports limit the damage." },
    ],
  },
];
